import React from 'react';
import { Phone, Mail, Users, Clock, Info, Zap } from 'lucide-react';
import { useFollowUpStats } from '../../../hooks/contacts/useFollowUpDecisions';
import { sumFollowUpTypes } from '../../../api/nextFollowUp';
import { useOrganizationStore, useFlywheelEnabled } from '../../../stores/useOrganizationStore';
import { Tooltip } from '../../common/Tooltip';
import { formatCallDuration } from '../../../utils/duration';
import { formatNumber } from '../../../utils/format';

const SECONDS_PER_FOLLOWUP = 45;

interface FollowUpBannerProps {
  dateRange: { from: Date | null; to: Date | null };
  onClick?: () => void;
}

export const FollowUpBanner: React.FC<FollowUpBannerProps> = ({ dateRange, onClick }) => {
  const { organization } = useOrganizationStore();
  const flywheelEnabled = useFlywheelEnabled();
  const assistantName = organization?.assistant_name || 'Remi';

  const FLYWHEEL_EXPLANATION = `The Follow-up Flywheel is ${assistantName}'s automated follow-up system. ${assistantName} analyzes each contact's activity (calls, website visits, emails) and schedules follow-ups at optimal times. This continues automatically until you hand off or remove contacts from the flywheel.`;

  const { data: statsData, isLoading } = useFollowUpStats({ dateRange });

  const data = statsData
    ? {
        total: statsData.summary.totalFollowUps,
        uniqueContacts: statsData.summary.uniqueContacts,
        calls: sumFollowUpTypes(statsData.byChannel, [
          'voice',
          'call',
          'schedule_call',
          'schedule_voice',
        ]),
        emails: sumFollowUpTypes(statsData.byChannel, ['email', 'send_email', 'schedule_email']),
        texts: sumFollowUpTypes(statsData.byChannel, [
          'sms',
          'text',
          'send_sms',
          'schedule_sms',
          'send_text',
          'schedule_text',
        ]),
      }
    : null;

  if (!flywheelEnabled || isLoading || !data || data.total === 0) {
    return null;
  }

  const timeSaved = formatCallDuration(data.total * SECONDS_PER_FOLLOWUP);

  return (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={onClick ? (e) => e.key === 'Enter' && onClick() : undefined}
      className={`followup-banner ${onClick ? 'cursor-pointer' : ''}`}
    >
      {/* Left: Icon + headline + stats */}
      <div className="followup-hero">
        <div className="btn-ai-gradient rounded-lg sm:rounded-xl p-2 sm:p-2.5 flex-shrink-0">
          <Zap className="h-4 w-4 sm:h-5 sm:w-5 text-white dark:text-zinc-900" />
        </div>
        <div className="min-w-0">
          <p className="text-sm sm:text-base font-semibold text-foreground">
            <span className="followup-accent">{assistantName} (AI)</span> has{' '}
            <span className="followup-accent">
              {formatNumber(data.total)} {data.total === 1 ? 'follow up' : 'follow ups'}
            </span>{' '}
            scheduled
          </p>
          {/* Desktop: single line with dots */}
          <div className="hidden sm:flex items-center gap-1.5 mt-0.5 text-muted-foreground text-sm flex-wrap">
            <Clock className="h-3.5 w-3.5" />
            <span>
              Saving you <span className="font-medium text-foreground">{timeSaved}</span>
            </span>
            {data.uniqueContacts > 0 && (
              <>
                <span className="followup-dot" />
                <Users className="h-3.5 w-3.5 text-purple-600 dark:text-purple-400" />
                <span>
                  <span className="font-medium text-foreground">
                    {formatNumber(data.uniqueContacts)}
                  </span>{' '}
                  {data.uniqueContacts === 1 ? 'contact' : 'contacts'}
                </span>
              </>
            )}
            {data.calls > 0 && (
              <>
                <span className="followup-dot" />
                <Phone className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                <span>
                  <span className="font-medium text-foreground">{formatNumber(data.calls)}</span>{' '}
                  {data.calls === 1 ? 'call' : 'calls'}
                </span>
              </>
            )}
            {data.emails > 0 && (
              <>
                <span className="followup-dot" />
                <Mail className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
                <span>
                  <span className="font-medium text-foreground">{formatNumber(data.emails)}</span>{' '}
                  {data.emails === 1 ? 'email' : 'emails'}
                </span>
              </>
            )}
          </div>
          {/* Mobile: stacked rows */}
          <div className="flex sm:hidden flex-col gap-1 mt-1 text-muted-foreground text-xs">
            <div className="flex items-center gap-1.5">
              <Clock className="h-3 w-3" />
              <span>
                Saving you <span className="font-medium text-foreground">{timeSaved}</span>
              </span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {data.uniqueContacts > 0 && (
                <span className="flex items-center gap-1">
                  <Users className="h-3 w-3 text-purple-600 dark:text-purple-400" />
                  <span className="font-medium text-foreground">
                    {formatNumber(data.uniqueContacts)}
                  </span>{' '}
                  {data.uniqueContacts === 1 ? 'contact' : 'contacts'}
                </span>
              )}
              {data.calls > 0 && (
                <span className="flex items-center gap-1">
                  <Phone className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
                  <span className="font-medium text-foreground">
                    {formatNumber(data.calls)}
                  </span>{' '}
                  {data.calls === 1 ? 'call' : 'calls'}
                </span>
              )}
              {data.emails > 0 && (
                <span className="flex items-center gap-1">
                  <Mail className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                  <span className="font-medium text-foreground">
                    {formatNumber(data.emails)}
                  </span>{' '}
                  {data.emails === 1 ? 'email' : 'emails'}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Right: Info tooltip */}
      <Tooltip content={FLYWHEEL_EXPLANATION} position="bottom">
        <Info className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-muted-foreground hover:text-amber-500 cursor-help flex-shrink-0" />
      </Tooltip>
    </div>
  );
};
